import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarIcon, Check, ChevronsUpDown, Copy } from "lucide-react";
import { format, parse } from "date-fns";
import { fr } from "date-fns/locale";
import {
  createOwnCabinet,
  declareEtablissement,
  endEtablissementLiaison,
  getMesEtablissements,
  type CreateOwnCabinetResult,
  type MonEtablissementItem,
} from "../../../api/professional";
import { getEtablissementDirectory, type PublicEtablissementSummary } from "../../../api/authEtablissement";
import { showToast } from "../../../components/ui/toastHotSonner";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../../components/ui/command";
import { Calendar } from "../../../components/ui/calendar";
import { useProfessionalAuth } from "../../../store/authProfessionalStore";
import OfficialiserCabinetModal from "../../../features/pro/OfficialiserCabinetModal";

type FormState = {
  etablissement: PublicEtablissementSummary | null;
  poste: string;
  dateDebut: string;
  commentaire: string;
};

const emptyForm: FormState = { etablissement: null, poste: "", dateDebut: "", commentaire: "" };

type AddMode = "search" | "cabinet";

const emptyCabinetForm = { nom: "", adresse: "", telephone: "" };

function formatDateFR(input?: string | null): string {
  if (!input) return "";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  return d.toLocaleDateString("fr-FR");
}

function statutBadge(statut?: string) {
  if (statut === "en_attente") {
    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
        style={{ background: "#FEF3C7", color: "#92400E" }}
      >
        En attente de validation
      </span>
    );
  }
  if (statut === "refusee") {
    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
        style={{ background: "#FEE2E2", color: "#991B1B" }}
      >
        Refusée — vous pouvez redéclarer
      </span>
    );
  }
  return null;
}

export default function MesEtablissements({ nip }: { nip: string }) {
  const { professional } = useProfessionalAuth();
  const [officializeOpen, setOfficializeOpen] = useState(false);
  const [items, setItems] = useState<MonEtablissementItem[]>([]);
  // Liaisons non 'active' déclarées/mises à jour dans CETTE session — le GET ne renvoie que les actives,
  // donc c'est la seule façon d'afficher un retour immédiat "en attente" sans nouvel endpoint backend.
  const [pendingItems, setPendingItems] = useState<MonEtablissementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingLiaisonId, setEditingLiaisonId] = useState<string | null>(null);
  const [addMode, setAddMode] = useState<AddMode>("search");
  const [form, setForm] = useState<FormState>(emptyForm);
  const [query, setQuery] = useState("");
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [directory, setDirectory] = useState<PublicEtablissementSummary[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [cabinetForm, setCabinetForm] = useState(emptyCabinetForm);
  const [cabinetSubmitting, setCabinetSubmitting] = useState(false);
  const [cabinetError, setCabinetError] = useState<string | null>(null);
  const [cabinetResult, setCabinetResult] = useState<CreateOwnCabinetResult | null>(null);
  const [passwordCopied, setPasswordCopied] = useState(false);

  const [endTarget, setEndTarget] = useState<MonEtablissementItem | null>(null);
  const [ending, setEnding] = useState(false);

  const selectedDateDebut = useMemo(() => {
    if (!form.dateDebut) return undefined;
    const parsed = parse(form.dateDebut, "yyyy-MM-dd", new Date());
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }, [form.dateDebut]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getMesEtablissements(nip);
    if (!res.ok) {
      setError(res.message);
      setLoading(false);
      return;
    }
    setItems(res.data);
    setLoading(false);
  }, [nip]);

  useEffect(() => { void load(); }, [load]);

  // Établissements déjà revendiqués (vrais comptes actifs) uniquement — recherche serveur par
  // nom (debounce 300ms), rechargée à chaque frappe et à l'ouverture de l'onglet.
  useEffect(() => {
    if (!formOpen || editingLiaisonId || addMode !== "search") return;
    let cancelled = false;
    setDirectoryLoading(true);
    const handle = setTimeout(() => {
      void getEtablissementDirectory({ estRevendique: true, limit: 20, q: query.trim() || undefined }).then((res) => {
        if (cancelled) return;
        setDirectoryLoading(false);
        if (res.ok) setDirectory(res.data.data);
      });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [formOpen, editingLiaisonId, addMode, query]);

  // Fusion : le GET (actives) prime sur pendingItems si un id devient actif entre-temps.
  const displayItems = useMemo(() => {
    const activeIds = new Set(items.map((i) => i.liaison.id));
    const merged = [...items, ...pendingItems.filter((p) => !activeIds.has(p.liaison.id) && p.liaison.statut !== "active")];
    return merged;
  }, [items, pendingItems]);

  const openAddForm = () => {
    setEditingLiaisonId(null);
    setAddMode("search");
    setForm(emptyForm);
    setQuery("");
    setComboboxOpen(false);
    setCalendarOpen(false);
    setFormError(null);
    setCabinetForm(emptyCabinetForm);
    setCabinetError(null);
    setCabinetResult(null);
    setPasswordCopied(false);
    setFormOpen(true);
  };

  const openEditForm = (item: MonEtablissementItem) => {
    setEditingLiaisonId(item.liaison.id);
    setForm({
      etablissement: item.etablissement,
      poste: item.liaison.poste ?? "",
      dateDebut: item.liaison.dateDebut ? item.liaison.dateDebut.slice(0, 10) : "",
      commentaire: item.liaison.commentaire ?? "",
    });
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    const hadCabinetResult = cabinetResult !== null;
    setFormOpen(false);
    setEditingLiaisonId(null);
    setForm(emptyForm);
    setQuery("");
    setComboboxOpen(false);
    setCalendarOpen(false);
    setFormError(null);
    setCabinetForm(emptyCabinetForm);
    setCabinetError(null);
    setCabinetResult(null);
    setPasswordCopied(false);
    if (hadCabinetResult) void load();
  };

  const handleCreateCabinet = async () => {
    if (!cabinetForm.nom.trim()) {
      setCabinetError("Le nom du cabinet est requis.");
      return;
    }
    setCabinetSubmitting(true);
    setCabinetError(null);
    const res = await createOwnCabinet(nip, {
      nom: cabinetForm.nom.trim(),
      adresse: cabinetForm.adresse.trim() || undefined,
      telephone: cabinetForm.telephone.trim() || undefined,
    });
    setCabinetSubmitting(false);
    if (!res.ok) {
      setCabinetError(res.message);
      return;
    }
    setCabinetResult(res.data);
    showToast("Cabinet médical créé, actif immédiatement.", "success");
  };

  const handleCopyPassword = async () => {
    if (!cabinetResult) return;
    try {
      await navigator.clipboard.writeText(cabinetResult.motDePasseInitial);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch {
      /* Presse-papiers indisponible (permissions navigateur) : le mot de passe reste affiché à l'écran. */
    }
  };

  const submitForm = async () => {
    if (!form.etablissement) {
      setFormError("Sélectionnez un établissement.");
      return;
    }
    setSubmitting(true);
    setFormError(null);
    const res = await declareEtablissement(nip, {
      etablissementId: form.etablissement.id,
      poste: form.poste.trim() || undefined,
      dateDebut: form.dateDebut || undefined,
      commentaire: form.commentaire.trim() || undefined,
    });
    setSubmitting(false);
    if (!res.ok) {
      if (res.errorCode === "ETABLISSEMENT_NON_REVENDIQUE") {
        setFormError("Cet établissement n'a pas encore de compte associé et ne peut pas être sélectionné.");
      } else if (res.status === 404) {
        setFormError("Établissement non trouvé.");
      } else {
        setFormError(res.message);
      }
      return;
    }
    const liaison = res.data; // statut réel renvoyé par le serveur — ne jamais le supposer
    const selectedEtablissement = form.etablissement;
    if (liaison.statut === "active") {
      setPendingItems((prev) => prev.filter((p) => p.liaison.id !== liaison.id));
    } else {
      setPendingItems((prev) => [
        ...prev.filter((p) => p.liaison.id !== liaison.id && p.etablissement.id !== selectedEtablissement.id),
        { etablissement: selectedEtablissement, liaison },
      ]);
    }
    showToast(
      liaison.statut === "active"
        ? "Établissement mis à jour."
        : "Demande envoyée, en attente de validation par l'établissement.",
      "success"
    );
    closeForm();
    await load();
  };

  const confirmEnd = async () => {
    if (!endTarget) return;
    setEnding(true);
    const res = await endEtablissementLiaison(nip, endTarget.liaison.id);
    setEnding(false);
    const target = endTarget;
    setEndTarget(null);
    if (!res.ok) {
      showToast(res.message || "Erreur lors de la fin d'affiliation.", "error");
      return;
    }
    setPendingItems((prev) => prev.filter((p) => p.liaison.id !== target.liaison.id));
    showToast("Affiliation terminée.", "success");
    await load();
  };

  return (
    <div className="mt-6 rounded-[12px] border bg-white p-6" style={{ borderColor: "#E5E7EB" }}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-semibold text-[#0B1F16]">Mes établissements</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOfficializeOpen(true)}
            className="h-9 px-4 rounded-[10px] font-semibold text-sm border"
            style={{ borderColor: "#00553E", color: "#00553E" }}
          >
            Officialiser mon établissement
          </button>
          <button
            type="button"
            onClick={openAddForm}
            className="h-9 px-4 rounded-[10px] font-semibold text-white text-sm"
            style={{ background: "#00553E" }}
          >
            Ajouter un établissement
          </button>
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Chaque déclaration doit être approuvée par l'établissement avant de devenir active.
      </p>

      {loading && <p className="mt-4 text-sm text-gray-500">Chargement…</p>}
      {error && (
        <div className="mt-4 rounded-[10px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {!loading && !error && displayItems.length === 0 && (
        <p className="mt-4 text-sm text-gray-500">Aucun établissement déclaré pour le moment.</p>
      )}

      {!loading && displayItems.length > 0 && (
        <div className="mt-4 space-y-2">
          {displayItems.map((item) => (
            <div
              key={item.liaison.id}
              className="rounded-[10px] border px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
              style={{ borderColor: "#E5E7EB" }}
            >
              <div>
                <div className="font-semibold text-[#0B1F16] flex items-center gap-2 flex-wrap">
                  {item.etablissement.nom}
                  {item.etablissement.sigle ? ` (${item.etablissement.sigle})` : ""}
                  {item.liaison.source === "cabinet_auto" && (
                    <span
                      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                      style={{ background: "#DCEFE9", color: "#00553E" }}
                    >
                      Votre cabinet
                    </span>
                  )}
                  {statutBadge(item.liaison.statut)}
                </div>
                <div className="text-xs text-gray-500">
                  {item.etablissement.type} — {item.etablissement.ville ?? item.etablissement.commune ?? "Localisation inconnue"}
                  {item.liaison.poste ? ` · ${item.liaison.poste}` : ""}
                  {item.liaison.dateDebut ? ` · depuis le ${formatDateFR(item.liaison.dateDebut)}` : ""}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => openEditForm(item)}
                  className="h-9 px-3 rounded-[10px] border text-sm"
                  style={{ borderColor: "#D1E2DD", color: "#00553E" }}
                >
                  Modifier
                </button>
                <button
                  type="button"
                  onClick={() => setEndTarget(item)}
                  className="h-9 px-3 rounded-[10px] border text-sm text-red-600"
                  style={{ borderColor: "#F3C0C0" }}
                >
                  {item.liaison.statut === "active" ? "Terminer" : "Annuler la demande"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal ajout/édition */}
      {formOpen && (
        <div className="fixed inset-0 z-20" onClick={closeForm}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div
              className="bg-white rounded-[12px] p-6 w-full max-w-md shadow space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-[#0B1F16]">
                {editingLiaisonId ? "Modifier l'affiliation" : "Déclarer un établissement"}
              </h3>

              {!editingLiaisonId && (
                <div className="inline-flex rounded-[10px] border bg-[#F9FAFA] p-1" style={{ borderColor: "#E5E7EB" }}>
                  {([
                    { id: "search" as const, label: "Rechercher établissement AGBAZA" },
                    { id: "cabinet" as const, label: "Ajouter votre cabinet" },
                  ]).map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setAddMode(t.id)}
                      className="h-9 px-3 rounded-[8px] text-xs font-semibold transition-colors"
                      style={{
                        background: addMode === t.id ? "#00553E" : "transparent",
                        color: addMode === t.id ? "#fff" : "#0B1F16",
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}

              {!editingLiaisonId && addMode === "search" && (
                <div className="space-y-2">
                  <label className="text-sm">Établissement</label>
                  <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        role="combobox"
                        aria-expanded={comboboxOpen}
                        className="flex h-10 w-full items-center justify-between rounded-[10px] border px-3 text-sm"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        <span className={form.etablissement ? "text-[#0B1F16] truncate" : "text-gray-400"}>
                          {form.etablissement ? form.etablissement.nom : "Sélectionner un établissement…"}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Nom de l'établissement…"
                          value={query}
                          onValueChange={(v) => setQuery(v.toUpperCase())}
                          style={{ textTransform: "uppercase" }}
                        />
                        <CommandList>
                          {directoryLoading ? (
                            <div className="py-6 text-center text-sm text-gray-500">Chargement…</div>
                          ) : (
                            <>
                              <CommandEmpty>Aucun établissement trouvé.</CommandEmpty>
                              <CommandGroup>
                                {directory.map((r) => (
                                  <CommandItem
                                    key={r.id}
                                    value={r.id}
                                    onSelect={() => {
                                      setForm((f) => ({ ...f, etablissement: r }));
                                      setComboboxOpen(false);
                                    }}
                                  >
                                    <Check
                                      className="h-4 w-4 shrink-0"
                                      style={{
                                        color: "#00553E",
                                        opacity: form.etablissement?.id === r.id ? 1 : 0,
                                      }}
                                    />
                                    <div className="min-w-0">
                                      <div className="truncate font-medium text-[#0B1F16]">
                                        {r.nom}
                                        {r.sigle ? ` (${r.sigle})` : ""}
                                      </div>
                                      <div className="truncate text-xs text-gray-500">
                                        {r.type} — {r.ville ?? r.commune ?? "Localisation inconnue"}
                                      </div>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {editingLiaisonId && form.etablissement && (
                <div className="rounded-[10px] border px-3 py-2 text-sm font-medium text-[#0B1F16]" style={{ borderColor: "#DCEFE9", background: "#F3FAF7" }}>
                  {form.etablissement.nom}
                </div>
              )}

              {(editingLiaisonId || addMode === "search") && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm">Poste <span className="text-gray-400">(optionnel)</span></label>
                    <input
                      value={form.poste}
                      onChange={(e) => setForm((f) => ({ ...f, poste: e.target.value.toUpperCase() }))}
                      placeholder="Médecin vacataire"
                      className="w-full h-10 rounded-[10px] border px-3"
                      style={{ borderColor: "#E5E7EB", textTransform: "uppercase" }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm">Date de début <span className="text-gray-400">(optionnel)</span></label>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="w-full h-10 rounded-[10px] border px-3 flex items-center justify-between text-left text-sm"
                          style={{ borderColor: "#E5E7EB" }}
                        >
                          <span className={selectedDateDebut ? "text-[#0B1F16]" : "text-gray-400"}>
                            {selectedDateDebut ? format(selectedDateDebut, "d MMMM yyyy", { locale: fr }) : "jj/mm/aaaa"}
                          </span>
                          <CalendarIcon size={16} className="text-gray-500" aria-hidden />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-auto p-0">
                        <Calendar
                          mode="single"
                          locale={fr}
                          selected={selectedDateDebut}
                          defaultMonth={selectedDateDebut ?? new Date()}
                          captionLayout="dropdown"
                          startMonth={new Date(new Date().getFullYear() - 50, 0)}
                          endMonth={new Date(new Date().getFullYear() + 1, 11)}
                          onSelect={(date) => {
                            setForm((f) => ({ ...f, dateDebut: date ? format(date, "yyyy-MM-dd") : "" }));
                            setCalendarOpen(false);
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm">Commentaire <span className="text-gray-400">(optionnel)</span></label>
                    <textarea
                      value={form.commentaire}
                      onChange={(e) => setForm((f) => ({ ...f, commentaire: e.target.value.toUpperCase() }))}
                      className="w-full min-h-[70px] rounded-[10px] border px-3 pt-2"
                      style={{ borderColor: "#E5E7EB", textTransform: "uppercase" }}
                    />
                  </div>

                  {formError && (
                    <div className="rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                      {formError}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => void submitForm()}
                      disabled={submitting || !form.etablissement}
                      className="h-11 rounded-[10px] font-semibold text-white disabled:opacity-60"
                      style={{ background: "#00553E" }}
                    >
                      {submitting ? "Enregistrement…" : "Enregistrer"}
                    </button>
                    <button
                      type="button"
                      onClick={closeForm}
                      className="h-11 rounded-[10px] font-semibold border"
                      style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
                    >
                      Annuler
                    </button>
                  </div>
                </>
              )}

              {!editingLiaisonId && addMode === "cabinet" && !cabinetResult && (
                <div className="space-y-4">
                  <p className="text-xs text-gray-500">
                    Votre cabinet devient immédiatement votre établissement de rattachement — aucune validation à
                    attendre, contrairement à une déclaration sur un établissement existant.
                  </p>
                  <div className="space-y-2">
                    <label className="text-sm">Nom du cabinet <span className="text-red-500">*</span></label>
                    <input
                      value={cabinetForm.nom}
                      onChange={(e) => setCabinetForm((f) => ({ ...f, nom: e.target.value.toUpperCase() }))}
                      placeholder="Cabinet Dr. Ahounou"
                      className="w-full h-10 rounded-[10px] border px-3"
                      style={{ borderColor: "#E5E7EB", textTransform: "uppercase" }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm">Adresse <span className="text-gray-400">(optionnel)</span></label>
                    <input
                      value={cabinetForm.adresse}
                      onChange={(e) => setCabinetForm((f) => ({ ...f, adresse: e.target.value.toUpperCase() }))}
                      placeholder="Rue 123, Cotonou"
                      className="w-full h-10 rounded-[10px] border px-3"
                      style={{ borderColor: "#E5E7EB", textTransform: "uppercase" }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm">Téléphone <span className="text-gray-400">(optionnel)</span></label>
                    <input
                      value={cabinetForm.telephone}
                      onChange={(e) => setCabinetForm((f) => ({ ...f, telephone: e.target.value.toUpperCase() }))}
                      placeholder="90000000"
                      className="w-full h-10 rounded-[10px] border px-3"
                      style={{ borderColor: "#E5E7EB", textTransform: "uppercase" }}
                    />
                  </div>

                  {cabinetError && (
                    <div className="rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                      {cabinetError}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => void handleCreateCabinet()}
                      disabled={cabinetSubmitting || !cabinetForm.nom.trim()}
                      className="h-11 rounded-[10px] font-semibold text-white disabled:opacity-60"
                      style={{ background: "#00553E" }}
                    >
                      {cabinetSubmitting ? "Création…" : "Créer mon cabinet"}
                    </button>
                    <button
                      type="button"
                      onClick={closeForm}
                      className="h-11 rounded-[10px] font-semibold border"
                      style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {!editingLiaisonId && addMode === "cabinet" && cabinetResult && (
                <div className="space-y-4">
                  <div className="rounded-[10px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                    Cabinet « {cabinetResult.etablissement.nom} » créé et actif. Vous pouvez dès maintenant l'utiliser
                    comme établissement de rattachement.
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm">Compte de connexion du cabinet</label>
                    <div className="rounded-[10px] border px-3 py-2 text-sm text-[#0B1F16]" style={{ borderColor: "#E5E7EB" }}>
                      {cabinetResult.etablissement.email}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm">
                      Mot de passe initial <span className="text-gray-400">(affiché une seule fois)</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <div
                        className="flex-1 rounded-[10px] border px-3 py-2 text-sm font-mono text-[#0B1F16]"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        {cabinetResult.motDePasseInitial}
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleCopyPassword()}
                        className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-[10px] border"
                        style={{ borderColor: "#00553E", color: "#00553E" }}
                        aria-label="Copier le mot de passe"
                      >
                        {passwordCopied ? <Check size={16} /> : <Copy size={16} />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Notez-le maintenant si vous souhaitez vous connecter séparément à l'espace établissement de ce
                      cabinet — il ne sera plus jamais affiché.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeForm}
                    className="h-11 w-full rounded-[10px] font-semibold text-white"
                    style={{ background: "#00553E" }}
                  >
                    Terminé
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation fin d'affiliation */}
      {endTarget && (
        <div className="fixed inset-0 z-20">
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-[12px] p-6 w-full max-w-md shadow">
              <div className="text-lg font-semibold text-[#0B1F16]">
                {endTarget.liaison.statut === "active" ? "Terminer" : "Annuler la demande pour"} l'affiliation avec {endTarget.etablissement.nom} ?
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Vous pourrez la redéclarer plus tard, elle sera simplement marquée comme terminée.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold text-white disabled:opacity-60"
                  style={{ background: "#00553E" }}
                  disabled={ending}
                  onClick={() => void confirmEnd()}
                >
                  {ending ? "Traitement…" : "Confirmer"}
                </button>
                <button
                  type="button"
                  className="h-11 rounded-[10px] font-semibold border"
                  style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
                  onClick={() => setEndTarget(null)}
                  disabled={ending}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {officializeOpen && (
        <OfficialiserCabinetModal
          nip={nip}
          professionalNom={professional?.nom}
          professionalPrenom={professional?.prenom}
          professionalEmail={professional?.email}
          onClose={() => setOfficializeOpen(false)}
          onDone={() => void load()}
        />
      )}
    </div>
  );
}
